import { useQuery } from '@tanstack/react-query';
import { activeMapCountry, activeMapCountryConfig } from '@/data/active-country';

/**
 * F5.5 — Wikipedia photo for a comune / municipio.
 *
 * Tries the MediaWiki Action API on the active country's Wikipedia language
 * section first (CORS-enabled via origin=*), with a bare-name variant plus a
 * language-specific disambiguation suffix. Falls back to the REST
 * `page/summary` endpoint when neither variant yields a thumbnail. Returns
 * `null` when no photo can be resolved.
 *
 * The wiki language section is read from the active map country's
 * `wikiLang` (see `web/src/features/map-core/countries.ts` and
 * `activeMapCountryConfig()`), not hardcoded — Italian comuni live in
 * it.wikipedia, Spanish municipios in es.wikipedia, and articles about small
 * settlements rarely exist in the other section at all.
 *
 * No API key, no new dependencies — uses the project's TanStack Query setup.
 */

export type ComunePhoto = {
  url: string;
  pageUrl: string;
  attribution?: string;
};

type ActionQueryResponse = {
  query?: {
    pages?: Record<
      string,
      {
        pageid?: number;
        title?: string;
        thumbnail?: { source?: string; width?: number; height?: number };
        original?: { source?: string; width?: number; height?: number };
        missing?: string;
      }
    >;
  };
};

type RestSummaryResponse = {
  type?: string;
  title?: string;
  content_urls?: {
    desktop?: { page?: string };
    mobile?: { page?: string };
  };
  thumbnail?: { source?: string };
  originalimage?: { source?: string };
};

function actionApiFor(wikiLang: string): string {
  return `https://${wikiLang}.wikipedia.org/w/api.php`;
}

function restApiFor(wikiLang: string): string {
  return `https://${wikiLang}.wikipedia.org/api/rest_v1/page/summary`;
}

// Per-language disambiguation suffix tried after the bare name, verified by hand
// against the live REST API (2026-08-17):
//
// Italian comuni: "<Name>_(comune)" — e.g. https://it.wikipedia.org/api/rest_v1/page/summary/San_Giovanni_(comune)
//
// Spanish municipios: bare name already resolves the common case (including
// article-prefixed official names like "El Castell de Guadalest" — MediaWiki's
// own redirect handles it: https://es.wikipedia.org/api/rest_v1/page/summary/El_Castell_de_Guadalest
// → 200, title "Guadalest"). The suffix is only needed for municipios that
// share their name with their province (or, once, an autonomous community),
// where the bare title lands on a disambiguation page or an unrelated primary
// topic:
//   - https://es.wikipedia.org/api/rest_v1/page/summary/Le%C3%B3n → "Panthera leo" (wrong topic)
//   - https://es.wikipedia.org/api/rest_v1/page/summary/Cuenca → disambiguation
//   - https://es.wikipedia.org/api/rest_v1/page/summary/M%C3%A9rida → disambiguation
//   - https://es.wikipedia.org/api/rest_v1/page/summary/Santander → disambiguation
//   - https://es.wikipedia.org/api/rest_v1/page/summary/Guadalajara → disambiguation
// All five resolve correctly with the "(España)" suffix, e.g.
//   https://es.wikipedia.org/api/rest_v1/page/summary/Le%C3%B3n_(Espa%C3%B1a) → standard, "León (España)"
const TITLE_SUFFIX_BY_LANG: Record<string, string> = {
  it: '_(comune)',
  es: '_(España)',
};

const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i;

// Comune/municipio infoboxes usually expose the flag / coat of arms as the
// first image — those land in `pageimages` and pollute the hero slot. Reject
// anything that looks like a heraldic asset; force the consumer to fall back
// to a real photo (or none).
//
// Verified against live Spanish infoboxes (2026-08-17) — the flag filename
// pattern there is "Bandera_de_<X>", NOT the Italian "Bandiera_di_<X>":
//   - Toledo: https://es.wikipedia.org/api/rest_v1/page/summary/Toledo → thumbnail
//     "Bandera_de_Toledo1.svg" (would have leaked through without "bandera" below)
//   - León (España), Cuenca (España): same "Bandera_de_…svg" pattern
// "escudo" (shield/coat of arms) was already covered and confirmed present on
// the same pages once the flag is skipped.
const HERALDIC_PATTERN = /(flag|bandiera|bandera|stemma|coat[_ ]of[_ ]arms|insignia|wappen|crest|gonfalone|emblem|escudo|escutcheon|seal[_ ]of[_ ])/i;

function isValidImageUrl(url: string | undefined | null): url is string {
  if (!url) return false;
  if (!url.startsWith('https://')) return false;
  if (!IMAGE_EXT.test(url)) return false;
  // Last path component carries the file name on Wikimedia thumbs:
  //   .../commons/thumb/8/8c/Bandiera_di_Bormio.svg/400px-Bandiera_di_Bormio.svg.png
  // We test the whole URL — names always appear after the last `/` AND inside `/thumb/.../`.
  const lastSlash = url.lastIndexOf('/');
  const filenamePart = lastSlash >= 0 ? url.slice(lastSlash + 1) : url;
  const decoded = (() => {
    try { return decodeURIComponent(filenamePart); } catch { return filenamePart; }
  })();
  if (HERALDIC_PATTERN.test(decoded)) return false;
  // Also check the path segment that holds the original filename inside Wikimedia's
  // thumb URLs (between /thumb/<x>/<xx>/<NAME>/<size>px-<NAME>).
  if (HERALDIC_PATTERN.test(url)) return false;
  return true;
}

function pageUrlForTitle(title: string, wikiLang: string): string {
  return `https://${wikiLang}.wikipedia.org/wiki/${encodeURIComponent(title)}`;
}

async function fetchActionThumbnail(title: string, wikiLang: string): Promise<ComunePhoto | null> {
  const params = new URLSearchParams({
    action: 'query',
    format: 'json',
    origin: '*',
    prop: 'pageimages',
    pithumbsize: '400',
    piprop: 'thumbnail|original',
    titles: title,
  });
  const res = await fetch(`${actionApiFor(wikiLang)}?${params.toString()}`);
  if (!res.ok) return null;
  const data = (await res.json()) as ActionQueryResponse;
  const pages = data.query?.pages;
  if (!pages) return null;
  for (const page of Object.values(pages)) {
    if (page.missing !== undefined) continue;
    const thumb = page.thumbnail?.source ?? page.original?.source;
    if (isValidImageUrl(thumb)) {
      const resolvedTitle = page.title ?? title;
      return {
        url: thumb,
        pageUrl: pageUrlForTitle(resolvedTitle, wikiLang),
      };
    }
  }
  return null;
}

async function fetchSummaryThumbnail(title: string, wikiLang: string): Promise<ComunePhoto | null> {
  const res = await fetch(`${restApiFor(wikiLang)}/${encodeURIComponent(title)}`, {
    headers: { accept: 'application/json' },
  });
  if (!res.ok) return null;
  const data = (await res.json()) as RestSummaryResponse;
  if (data.type === 'disambiguation') return null;
  const thumb = data.thumbnail?.source ?? data.originalimage?.source;
  if (!isValidImageUrl(thumb)) return null;
  const pageUrl =
    data.content_urls?.desktop?.page ??
    data.content_urls?.mobile?.page ??
    pageUrlForTitle(data.title ?? title, wikiLang);
  return { url: thumb, pageUrl };
}

export async function fetchComunePhoto(name: string, wikiLang: string): Promise<ComunePhoto | null> {
  // REST summary first — Wikimedia curates a `page_image_free` that's usually a
  // real photo (Panorama/Veduta) rather than the infobox's first asset.
  const suffix = TITLE_SUFFIX_BY_LANG[wikiLang];
  const variants = suffix ? [name, `${name.replaceAll(' ', '_')}${suffix}`] : [name];
  for (const variant of variants) {
    try {
      const hit = await fetchSummaryThumbnail(variant, wikiLang);
      if (hit) return hit;
    } catch {
      // try next variant
    }
  }
  // Fallback: Action API pageimages (filtered for heraldic noise via isValidImageUrl).
  for (const variant of variants) {
    try {
      const hit = await fetchActionThumbnail(variant, wikiLang);
      if (hit) return hit;
    } catch {
      // try next variant
    }
  }
  return null;
}

const DAY = 24 * 60 * 60 * 1000;

export function useComunePhoto(name: string | undefined | null) {
  const wikiLang = activeMapCountryConfig().wikiLang;
  return useQuery<ComunePhoto | null>({
    queryKey: ['wiki-photo', activeMapCountry(), name ?? ''],
    queryFn: () => fetchComunePhoto(name as string, wikiLang),
    enabled: !!name,
    staleTime: Infinity,
    gcTime: DAY,
    retry: false,
  });
}
